import {Country, CreationChannel, Product, User} from '@hconnect/apiclient'
import {trackEvent} from '@hconnect/common/logging/Analytics'
import {LoadingButton, Typography} from '@hconnect/uikit'
import {Box, Button} from '@mui/material'
import {isEmpty} from 'lodash'
import React, {useEffect, useState} from 'react'
import {useFieldArray, useForm} from 'react-hook-form'
import {useSelector} from 'react-redux'
import {useHistory} from 'react-router-dom'

import {isProd} from '../../common/Constants'
import {useCountriesConfiguration} from '../../common/hooks/useCountriesConfiguration'
import {useUserRoles} from '../../common/hooks/useUserRoles'
import {useUserInformationFormData} from '../../components/UserInformation/hooks/useUserInformationFormData'
import {useTranslation} from '../../hooks/useTranslation'
import {
  selectLoggedInUserPermissions,
  selectLoggedInUserProfile
} from '../../modules/LoggedInUser.selectors'
import {RoleAssignment, RoleType} from '../../modules/ManageUsers.selectors'

import {WidgetUserCustomerIds} from './components/WidgetUserCustomerIds'
import {WidgetUserFormFields} from './components/WidgetUserFormFields'
import {WidgetUserProductsPermissions} from './components/WidgetUserProductsPermissions'
import {InviteUserPayload, useCreateWidgetUser} from './hooks/useCreateWidgetUser'
import {
  useAutoSelectCountry,
  usePermissionSync,
  useProductValidation
} from './hooks/useWidgetUserFormEffects'
import {customerRoleSetup} from './utils/CustomerRoleSetup'
import {useWidgetUserCreationStyles} from './WidgetUserCreation.styles'
import {
  CustomerToggleBasedPermissionKeys,
  CustomerToggleBasedPermissions,
  DataScopeOption
} from './WidgetUserCreation.types'

// Helper function to build user role assignments
const buildUserRoleAssignments = (
  data: WidgetUserCreationFormData,
  selectedOrgUnit: DataScopeOption | undefined
): Omit<RoleAssignment, 'userId'>[] => {
  return customerRoleSetup(data.permissions).flatMap((roleType) => {
    const areSiteIdsPresent =
      data.customerIds.filter((customerId) => customerId.siteIds.length > 0).length > 0

    return areSiteIdsPresent
      ? data.customerIds.map((customerId) => ({
          id: -1,
          roleType: roleType as RoleType,
          dataScope: {
            countryId: data.country,
            businessLine: data.businessLine,
            orgUnitId: selectedOrgUnit?.value || '',
            customerIds: [customerId.customerId],
            siteIds: customerId.siteIds
          }
        }))
      : {
          id: -1,
          roleType: roleType as RoleType,
          dataScope: {
            countryId: data.country,
            businessLine: data.businessLine,
            orgUnitId: selectedOrgUnit?.value || '',
            customerIds: data.customerIds.map((customerData) => customerData.customerId) || ['']
          }
        }
  })
}

// Helper function to build user data
const buildUserData = (data: WidgetUserCreationFormData): Partial<User> => ({
  name: data.name,
  eMail: data.email,
  mobileNumber: data.mobileNumber,
  country: data.country,
  creationProduct: 'HConnect',
  isTermsApprovalRequired: true,
  creationChannel: 'CSC' as CreationChannel,
  isTester: !isProd,
  marketId: data.market,
  companyName: data.companyName,
  primaryOrganisationalUnit: data.dispatchPhoneKey,
  defaultBranding: data.branding
})

export type WidgetUserCreationFormData = {
  name: string
  email: string
  mobileNumber: string
  customerIds: CustomerData[]
  businessLine: string
  country: string
  orgUnit: string
  products: Product[]
  permissions: CustomerToggleBasedPermissions
  companyName: string
  branding: string
  market: string
  dispatchPhoneKey: string
  availableCountries?: Array<Country> | null
}

type CustomerData = {
  customerId: string
  siteIds: string[]
}

export const WidgetUserCreationForm = () => {
  const {t} = useTranslation()
  const {classes} = useWidgetUserCreationStyles()
  const loggedInUserProfile = useSelector(selectLoggedInUserProfile)
  const history = useHistory()

  const {data: availableCountries, isLoading: loadingAvailableCountries} =
    useCountriesConfiguration()

  const [cscData, setCscData] = useState<RoleAssignment[]>()
  const cscCountries = cscData
    ?.map((data) => data.dataScope['countryId'])
    .filter((country, index, countries) => countries.indexOf(country) === index) as
    | string[]
    | undefined

  const cscBusinessLines = cscData
    ?.map((data) => data.dataScope['businessLine'])
    .filter(
      (businessLine, index, businessLines) => businessLines.indexOf(businessLine) === index
    ) as string[] | undefined
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>()
  const [selectedCustomerNumber, setSelectedCustomerNumber] = useState<string | undefined>()

  const [selectedOrgUnit, setSelectedOrgUnit] = useState<DataScopeOption>()
  const [selectedProducts, setSelectedProducts] = useState<Product[]>()

  const initPermissions = {
    [CustomerToggleBasedPermissionKeys.canSeeOrderAndDeliveries]: true,
    [CustomerToggleBasedPermissionKeys.canCreateOrders]: false,
    [CustomerToggleBasedPermissionKeys.canChangeOrders]: false,
    [CustomerToggleBasedPermissionKeys.canSeeInvoices]: false
  }

  const formMethods = useForm<WidgetUserCreationFormData>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      mobileNumber: '',
      customerIds: [{customerId: '', siteIds: []}],
      orgUnit: '',
      businessLine: '',
      country: '',
      products: [],
      permissions: initPermissions,
      availableCountries: availableCountries
    }
  })

  const {brandings, marketSelectValues, primaryOrgUnitsMarketsSelectValues} =
    useUserInformationFormData(formMethods.watch(), loggedInUserProfile)

  // Update availableCountries in form when it loads
  useEffect(() => {
    if (availableCountries && availableCountries.length > 0) {
      const currentAvailableCountries = formMethods.watch('availableCountries')
      if (!currentAvailableCountries || currentAvailableCountries.length === 0) {
        formMethods.setValue('availableCountries', availableCountries, {shouldDirty: false})
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCountries])

  const {fields, append, remove} = useFieldArray({
    name: 'customerIds' as const,
    control: formMethods.control
  })

  const {
    handleCreateUser,
    isLoading: isLoadingCreateUser,
    isUserCreated
  } = useCreateWidgetUser(formMethods)

  const {data: roles} = useUserRoles(loggedInUserProfile?.user_id)

  useEffect(() => {
    if (roles && roles?.length > 0) {
      const cscRoles = roles?.filter(
        (role) =>
          role.roleType === 'CUSTOMER_SERVICE_CENTER' ||
          role.roleType === 'CUSTOMER_SERVICE_CENTER_RO' ||
          role.roleType === 'SALES_AGENT'
      )
      setCscData(cscRoles)
    }
  }, [roles])

  const loggedInUserPermissions = useSelector(selectLoggedInUserPermissions)
  const canAssignRoles = loggedInUserPermissions.some((p) => p.permissionType === 'ASSIGN_ROLES')

  // Auto-select country if only one CSC country is available
  useAutoSelectCountry(cscCountries, formMethods)

  // Reset customer ID field when customer number is cleared
  useEffect(() => {
    if (selectedCustomerId && !selectedCustomerNumber) {
      const customerIds = formMethods.getValues('customerIds')
      const customerIndex = customerIds.findIndex(
        (customerData) => customerData.customerId === selectedCustomerId
      )

      if (customerIndex !== -1) {
        formMethods.resetField(`customerIds.${customerIndex}`, {
          defaultValue: {customerId: '', siteIds: []}
        })
      }
      setSelectedCustomerId(undefined)
    }
  }, [selectedCustomerNumber, selectedCustomerId, formMethods])

  // Reset customer number field when org unit is selected
  useEffect(() => {
    if (selectedOrgUnit && selectedCustomerNumber && selectedCustomerId) {
      const customerIds = formMethods.getValues('customerIds')
      const customerIndex = customerIds.findIndex(
        (customerData) => customerData.customerId === selectedCustomerId
      )

      if (customerIndex !== -1) {
        formMethods.resetField(`customerIds.${customerIndex}`, {
          defaultValue: {customerId: '', siteIds: []}
        })
      }

      setSelectedCustomerNumber(undefined)
    }
  }, [selectedOrgUnit, selectedCustomerNumber, selectedCustomerId, formMethods])

  // Handle product validation
  useProductValidation(selectedProducts, formMethods)

  const products = formMethods.watch('products')
  const selectedCountry = formMethods.watch('country')
  const selectedBusinessLine = formMethods.watch('businessLine')

  // Check if user has required roles
  const hasRequiredRoles = cscData && cscData.length > 0

  const handleSubmitForm = async (data: WidgetUserCreationFormData) => {
    const userData = buildUserData(data)
    const assignUserRolesData = buildUserRoleAssignments(data, selectedOrgUnit)
    const inviteUserData: Omit<InviteUserPayload, 'user_id'>[] = data.products.map((product) => ({
      product
    }))

    trackEvent('cscWidgetUserCreationFormSubmit', {
      userId: loggedInUserProfile?.user_id,
      product: 'adminconsole',
      createUserData: JSON.stringify(userData),
      assignUserRolesData: JSON.stringify(assignUserRolesData),
      inviteUserData: JSON.stringify(inviteUserData)
    })

    await handleCreateUser({
      createUser: userData,
      assignUserRoles: assignUserRolesData,
      inviteUser: inviteUserData
    })
  }

  const areCustomerIdsValid = formMethods
    .watch('customerIds')
    .every((data) => !!data.customerId.split('.')[1])

  const isCreateOrderPermissionSelected = formMethods.watch('permissions')?.canCreateOrders

  // Sync permission changes
  usePermissionSync(!!isCreateOrderPermissionSelected, formMethods)

  return (
    <form onSubmit={formMethods.handleSubmit(handleSubmitForm)}>
      {!hasRequiredRoles && roles && roles.length > 0 && (
        <Box
          style={{
            padding: '16px',
            marginBottom: '24px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px'
          }}
        >
          <Typography style={{fontSize: '16px', fontWeight: 600, color: '#856404'}}>
            ⚠️ {t('widgetUserCreation.form.errors.noRequiredRoles', 'Missing Required Roles')}
          </Typography>
          <Typography style={{fontSize: '14px', color: '#856404', marginTop: '8px'}}>
            {t('widgetUserCreation.form.errors.noRequiredRolesDescription')}
          </Typography>
        </Box>
      )}
      <WidgetUserFormFields
        formMethods={formMethods}
        selectedProducts={selectedProducts}
        cscCountries={cscCountries}
        brandings={brandings}
        loadingAvailableCountries={loadingAvailableCountries}
        marketSelectValues={marketSelectValues}
        primaryOrgUnitsMarketsSelectValues={primaryOrgUnitsMarketsSelectValues}
        cscBusinessLines={cscBusinessLines}
        selectedCountry={selectedCountry}
        cscData={cscData}
        selectedOrgUnit={selectedOrgUnit}
        setSelectedOrgUnit={setSelectedOrgUnit}
        selectedBusinessLine={selectedBusinessLine}
      />
      <WidgetUserCustomerIds
        formMethods={formMethods}
        fields={fields}
        append={append}
        remove={remove}
        selectedOrgUnit={selectedOrgUnit}
        cscData={cscData}
      />
      <WidgetUserProductsPermissions
        formMethods={formMethods}
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
        initPermissions={
          !canAssignRoles
            ? Object.fromEntries(
                Object.entries(initPermissions).filter(
                  ([key]) => key === CustomerToggleBasedPermissionKeys.canSeeOrderAndDeliveries
                )
              )
            : initPermissions
        }
        isCreateOrderPermissionSelected={!!isCreateOrderPermissionSelected}
      />
      <Box
        style={{display: 'flex', gap: '12px', justifyContent: 'space-between', marginTop: '48px'}}
      >
        <Button
          className={classes.discardButton}
          variant="text"
          onClick={() => {
            trackEvent('cscWidgetUserCreationGoToSearch', {
              userId: loggedInUserProfile?.user_id,
              product: 'adminconsole'
            })
            history.push('/widgetUserList')
          }}
        >
          {t('widgetUserCreation.form.goBackToSearch')}
        </Button>
        {!isUserCreated && (
          <LoadingButton
            type="submit"
            btnClassName={classes.submitButton}
            progressClassName={classes.progress}
            loading={isLoadingCreateUser}
            disabled={
              isEmpty(formMethods.formState.dirtyFields) ||
              !isEmpty(formMethods.formState.errors) ||
              !formMethods.formState.isValid ||
              products.length === 0 ||
              !areCustomerIdsValid
            }
          >
            <Typography
              style={{
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 500,
                letterSpacing: 0,
                color: '#FFFFFF'
              }}
            >
              {t('widgetUserCreation.form.create')}
            </Typography>
          </LoadingButton>
        )}
      </Box>
    </form>
  )
}
